import React, { useEffect } from 'react';
import { Wrapper } from '../../../components/Wrapper';
import {
  Box,
  Text,
  Input,
  InputGroup,
  Button,
  Flex,
  Icon,
  Select,
  Grid,
  useBreakpointValue,
  Img,
  GridItem,
} from '@chakra-ui/react';
import { FaSlidersH } from 'react-icons/fa';
import { If, Then, Else } from 'react-if';
import Skeleton from 'react-loading-skeleton';
import { useIsAuthenticated } from 'react-auth-kit';

import PengetahuanController, {
  usePengetahuanContext,
} from '../../../controller/pengetahuan/index';
import { GetLocalStorage } from '../../../../misc/UseLocalStorage';
import PengetahuanBackground from '../../../assets/webp/bg_daftar_pengetahuan.webp';
import PengetahuanBackgroundMobile from '../../../assets/webp/bg_daftar_pengetahuan_mobile.webp';
import LogoPintar from '../../../assets/png/logo_pintar_black.png';
import Pagination from '../../../components/Pagination';
import CardItem from '../../../components/global/Card/CardItem';
import CardItemSync from '../../../components/global/Card/CardItemSync';
import CategoryPengetahuan from '../../../components/Pengetahuan/CategoryPengetahuan';
import GuidePengetahuan from '../../../components/Pengetahuan/Guide';

/**
 * Component for displaying the hero section of the knowledge center page.
 * @returns {JSX.Element} The HeroPelatihan component.
 * @author Bagas Prasetyadi
 */

const HeroPengetahuan: React.FC = () => {
  const { setState, fetchData } = usePengetahuanContext();
  const isMobile = useBreakpointValue({ base: true, md: false });

  const onSubmitSearch = (e) => {
    e.preventDefault();
    fetchData({ jenis_pengetahuan: 'synchronized' });
  };

  return (
    <Box
      backgroundImage={{
        base: `url(${PengetahuanBackgroundMobile})`,
        md: `url(${PengetahuanBackground})`,
      }}
      h={{ base: '230px', sm: '350px', md: '52vh' }}
      w="auto"
      backgroundSize="cover"
      backgroundPosition={{ base: 'right', md: 'center' }}
      display="flex"
      justifyContent={{ base: 'center', md: 'start' }}
      alignItems="center"
    >
      <Flex
        justifyContent="center"
        alignItems={{ base: 'center', md: 'start' }}
        flexDirection="column"
        ml={{ base: 0, md: '8rem' }}
        mt={{ base: '-50px', md: '-2rem' }}
        w={{ base: '90%', md: 'auto' }}
      >
        {isMobile ? null : <Img src={LogoPintar} w="150px" alt="Logo Pintar" />}
        <Text
          mt={{ base: 0, md: 8 }}
          fontSize={{ base: '2xl', md: '5xl' }}
          fontWeight="bold"
          color="black"
          textAlign={{ base: 'center', md: 'left' }}
          letterSpacing="tight"
        >
          Mari Belajar Melalui Pusat
          <br />
          Pengetahuan
        </Text>
        <Box mt={{ base: 2, md: 6 }} w="full">
          <form onSubmit={(e) => onSubmitSearch(e)} id="search-pengetahuan">
            <InputGroup h={{ base: 8, md: 12 }}>
              <Input
                type="text"
                placeholder="Cari Materi"
                onChange={(e) => {
                  setState((prev) => ({
                    ...prev,
                    keyword: e.target.value,
                  }));
                }}
                pr="8rem"
                bg="white"
                border="1px"
                borderColor="green.700"
                rounded="xl"
                h="full"
                w="90%"
                mr={3}
                fontSize="sm"
                _hover={{ border: 'green.700' }}
                _focus={{
                  borderColor: 'green.700',
                  ring: '1px',
                  ringColor: 'green.700',
                }}
              />

              <Button
                rounded="xl"
                w={{ base: 16, md: 20 }}
                h="full"
                type="submit"
                bg="green.700"
                color="white"
                _hover={{ bg: 'green.800' }}
                _active={{ bg: 'green.800' }}
              >
                Cari
              </Button>
            </InputGroup>
          </form>
        </Box>
      </Flex>
    </Box>
  );
};

const GridPengetahuan = ({ listData, isLoading }) => {
  return (
    <>
      <div className="mt-4">
        <If condition={isLoading}>
          <Then>
            <Skeleton
              count={3}
              style={{ height: '400px', width: '280px', marginRight: '20px' }}
              inline={true}
            />
          </Then>
          <Else>
            <If condition={listData.length > 0}>
              <Then>
                <Grid
                  gap={4}
                  templateColumns={{
                    base: 'repeat(1fr)',
                    sm: 'repeat(2, 1fr)',
                    md: 'repeat(3, 1fr)',
                  }}
                >
                  {listData.map((item, key) => {
                    return item.jenis_pengetahuan === 'synchronized' ? (
                      <CardItemSync
                        item={item}
                        spacing={false}
                        key={key}
                        accessFor="pengetahuan"
                      />
                    ) : (
                      <CardItem
                        item={item}
                        spacing={false}
                        key={key}
                        accessFor="pengetahuan"
                      />
                    );
                  })}
                </Grid>
              </Then>
              <Else>
                <Box textAlign="center" mt={20}>
                  <Text fontSize="xl" color="gray.400">
                    Belum Ada Pengetahuan
                  </Text>
                </Box>
              </Else>
            </If>
          </Else>
        </If>
      </div>
    </>
  );
};

/**
 * Component for displaying the list of knowledge items.
 * @returns {JSX.Element} The ListPengetahuan component.
 */

const ListPengetahuan: React.FC = () => {
  const { fetchData, setState, getListKategoriPengetahuan } =
    usePengetahuanContext();
  const {
    isLoading,
    listData,
    category,
    pagination,
    page,
    perPage,
    kategori_pengetahuan_id,
    sort_by,
  } = usePengetahuanContext().state;

  const isMobile = useBreakpointValue({ base: true, md: false });

  const totalPage = pagination ? Math.ceil(pagination.total_rows / perPage) : 0;

  const handlePageChange = (e, type) => {
    setState((prev) => ({
      ...prev,
      [type]: e.selected + 1,
    }));
  };

  useEffect(() => {
    fetchData({ jenis_pengetahuan: 'asynchronized' });
  }, [category, kategori_pengetahuan_id, sort_by, page, perPage]);

  useEffect(() => {
    getListKategoriPengetahuan();
  }, []);

  return (
    <Box mx={{ base: 8, md: 12 }} my={8}>
      <Flex
        alignItems={{ base: 'start', md: 'center' }}
        justifyContent="space-between"
        w="full"
        flexDirection={{ base: 'column', md: 'row' }}
      >
        <Text fontSize="lg" fontWeight="semibold" color="green.700">
          {' '}
          Ditemukan {pagination?.total_rows} Pengetahuan
        </Text>

        <Box
          position="relative"
          display="flex"
          justifyContent="flex-end"
          alignItems="center"
          mb={{ base: 4, md: 0 }}
        >
          <Box position="relative" marginRight={4}>
            <Icon
              as={FaSlidersH}
              position="absolute"
              mt={3}
              ml={3}
              boxSize={4}
            />
            <Select
              w={{ base: '180px', md: '250px' }}
              h={10}
              border="1px"
              borderColor="gray.500"
              rounded="md"
              _focus={{
                ring: '1',
                borderColor: 'green.700',
                ringColor: 'green.700',
              }}
              _hover={{
                borderColor: 'gray.500',
              }}
              fontSize={{ base: 'sm', md: 'md' }}
              _placeholder={{ color: 'gray.300' }}
              style={{ paddingLeft: '40px' }}
              defaultValue=""
              onChange={(e) => {
                setState((prev) => ({
                  ...prev,
                  sort_by: e.target.value,
                }));
              }}
            >
              <option value="terbaru">Terbaru</option>
              <option value="penayangan terbanyak">Penayangan Terbanyak</option>
              <option value="rating tertinggi">Rating Tertinggi</option>
            </Select>
          </Box>

          {!isMobile && <GuidePengetahuan />}
        </Box>
      </Flex>
      <Grid templateColumns={'repeat(6, 1fr)'} gap={4} mt={4}>
        {/* Sidebar */}
        <GridItem colSpan={{ base: 6, md: 1 }}>
          <CategoryPengetahuan page="page" type="async" />
        </GridItem>
        <GridItem colSpan={{ base: 6, md: 5 }}>
          <Text fontSize={{ base: 'lg', md: '2xl' }} fontWeight="semibold">
            Pengetahuan Online Learning
          </Text>
          {isMobile && <GuidePengetahuan />}

          {/* Pengetahuan Async */}
          <GridPengetahuan listData={listData} isLoading={isLoading} />

          {pagination && totalPage > 1 && (
            <Flex justifyContent="end" mt={8}>
              <Pagination
                handleChange={(e) => handlePageChange(e, 'page')}
                totalPage={totalPage}
                page={page}
              />
            </Flex>
          )}
        </GridItem>
      </Grid>
    </Box>
  );
};

const WrapperMain: React.FC = () => {
  // use is authenticated
  const isAuthenticated = useIsAuthenticated();

  const statusPengisian = GetLocalStorage({ name: 'status_pengisian' });

  if (isAuthenticated() !== false && statusPengisian !== 'true') {
    window.location.href = '/profile';
  }

  return (
    <Wrapper>
      {/* Hero */}
      <HeroPengetahuan />

      {/* List Pengetahuan */}
      <ListPengetahuan />
    </Wrapper>
  );
};

const DaftarPengetahuan: React.FC = () => {
  return (
    <PengetahuanController.Provider>
      <WrapperMain />
    </PengetahuanController.Provider>
  );
};

export default DaftarPengetahuan;
